import type { ReactNode } from 'react'
import { ModeToggle } from '@/components/mode-toggle'
import { cn } from '@/lib/utils'
import { isTauri } from '@/lib/utils/tauri'

interface AuthLayoutProps {
  children: ReactNode
  showTestimonial?: boolean
  /** `wide` for decision screens with more than a form (OAuth consent);
   *  sign-in forms keep the narrow card. */
  size?: 'default' | 'wide'
}

export function AuthLayout({
  children,
  showTestimonial = false,
  size = 'default',
}: AuthLayoutProps) {
  const isWide = size === 'wide'
  const showModeToggle = !isTauri()

  return (
    <div
      className="relative h-[var(--tauri-content-height,100vh)] min-h-0 overflow-y-auto overflow-x-hidden bg-background"
      data-scroll-container
    >
      {/* Dark Mode Toggle - positioned in top-right */}
      {showModeToggle && (
        <div className="absolute right-4 top-4 z-20">
          <ModeToggle />
        </div>
      )}

      {/* Modern gradient background using theme colors with brand accents */}
      <div className="from-primary/12 via-muted/8 via-accent/6 to-primary/18 animate-gradient-slow dark:via-accent/12 absolute inset-0 bg-gradient-to-br dark:from-primary/20 dark:via-muted/15 dark:to-primary/25"></div>

      {/* Subtle brand color accent layers */}
      <div className="from-[hsl(var(--brand-blue))]/8 via-[hsl(var(--brand-green))]/6 to-[hsl(var(--brand-orange))]/8 animate-pulse-slow dark:from-[hsl(var(--brand-blue))]/12 dark:via-[hsl(var(--brand-green))]/8 dark:to-[hsl(var(--brand-orange))]/12 absolute inset-0 bg-gradient-to-tr via-transparent dark:via-transparent"></div>
      <div className="from-[hsl(var(--brand-red))]/6 via-[hsl(var(--brand-yellow))]/8 animate-gradient-slow animation-delay-3000 dark:via-[hsl(var(--brand-yellow))]/12 absolute inset-0 bg-gradient-to-tl to-transparent dark:from-[hsl(var(--brand-red))]/10 dark:to-transparent"></div>

      {/* Modern overlay for clean contrast */}
      <div className="absolute inset-0 bg-background/60 dark:bg-background/70"></div>

      {/* Modern subtle flowing shapes using theme and brand colors */}
      <div className="absolute left-0 top-0 h-full w-full overflow-hidden opacity-25 dark:opacity-20">
        {/* Large organic shapes with modern opacity */}
        <div className="to-muted/12 animate-blob animation-delay-1000 animate-float-gentle dark:to-muted/18 absolute left-[10%] top-[10%] h-96 w-96 rounded-full bg-gradient-to-br from-primary/20 mix-blend-overlay dark:from-primary/25"></div>
        <div className="from-[hsl(var(--brand-orange))]/18 to-[hsl(var(--brand-yellow))]/12 animate-blob animation-delay-3000 animate-float-reverse dark:from-[hsl(var(--brand-orange))]/22 dark:to-[hsl(var(--brand-yellow))]/16 absolute bottom-[15%] right-[15%] h-80 w-80 rounded-full bg-gradient-to-tr mix-blend-overlay"></div>
        <div className="from-[hsl(var(--brand-green))]/18 to-accent/12 animate-blob animation-delay-5000 animate-float-slow dark:from-[hsl(var(--brand-green))]/22 dark:to-accent/16 absolute left-[20%] top-[50%] h-72 w-72 rounded-full bg-gradient-to-bl mix-blend-overlay"></div>
        <div className="from-[hsl(var(--brand-red))]/18 to-destructive/12 animate-blob animation-delay-2000 animate-drift-horizontal dark:from-[hsl(var(--brand-red))]/22 dark:to-destructive/16 absolute right-[5%] top-[20%] h-64 w-64 rounded-full bg-gradient-to-br mix-blend-overlay"></div>
        <div className="from-[hsl(var(--brand-blue))]/18 to-primary/12 animate-blob animation-delay-4000 animate-drift-vertical dark:from-[hsl(var(--brand-blue))]/22 dark:to-primary/16 absolute bottom-[40%] left-[60%] h-56 w-56 rounded-full bg-gradient-to-tl mix-blend-overlay"></div>

        {/* Medium abstract shapes with refined opacity */}
        <div className="animate-blob animation-delay-6000 animate-float-gentle absolute right-[25%] top-[70%] h-48 w-48 rounded-full bg-gradient-to-r from-secondary/15 to-muted/10 mix-blend-overlay dark:from-secondary/20 dark:to-muted/15"></div>
        <div className="animate-blob animation-delay-7000 animate-float-reverse absolute left-[70%] top-[35%] h-40 w-40 rounded-full bg-gradient-to-l from-accent/15 to-primary/10 mix-blend-overlay dark:from-accent/20 dark:to-primary/15"></div>

        {/* Subtle grid pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGZpbGw9IiNmZmZmZmYwMyIgZD0iTTAgMGg2MHY2MEgweiIvPjxwYXRoIGQ9Ik0zNiAxOGgtMnYyNGgyVjE4em0tMTAgMGgydjI0aC0yVjE4ek0xOCAzNnYtMmgyNHYyaC0yNHptMC0xMGgyNHYyaC0yNHYtMnoiIGZpbGw9IiNmZmZmZmYwOCIvPjwvZz48L3N2Zz4=')] opacity-10 dark:opacity-15"></div>
      </div>

      {/* Modern subtle floating particles using theme colors */}
      <div className="absolute inset-0 overflow-hidden opacity-60 dark:opacity-50">
        <div className="particle bg-primary/12 dark:bg-primary/18 left-[45%] top-[15%]"></div>
        <div className="particle bg-[hsl(var(--brand-orange))]/12 animation-delay-2000 dark:bg-[hsl(var(--brand-orange))]/18 left-[20%] top-[35%]"></div>
        <div className="particle bg-[hsl(var(--brand-green))]/12 animation-delay-4000 dark:bg-[hsl(var(--brand-green))]/18 left-[60%] top-[70%]"></div>
        <div className="particle bg-[hsl(var(--brand-red))]/12 animation-delay-1000 dark:bg-[hsl(var(--brand-red))]/18 left-[80%] top-[20%]"></div>
        <div className="particle bg-accent/12 animation-delay-3000 dark:bg-accent/18 left-[10%] top-[60%]"></div>
        <div className="particle bg-[hsl(var(--brand-yellow))]/12 animation-delay-2500 dark:bg-[hsl(var(--brand-yellow))]/18 left-[25%] top-[85%]"></div>
        <div className="particle bg-secondary/12 animation-delay-1500 dark:bg-secondary/18 left-[70%] top-[40%]"></div>
        <div className="particle bg-[hsl(var(--brand-blue))]/12 animation-delay-3500 dark:bg-[hsl(var(--brand-blue))]/18 left-[30%] top-[15%]"></div>
        <div className="particle animation-delay-5000 left-[85%] top-[50%] bg-primary/10 dark:bg-primary/15"></div>
        <div className="particle animation-delay-6000 left-[5%] top-[75%] bg-[hsl(var(--brand-orange))]/10 dark:bg-[hsl(var(--brand-orange))]/15"></div>
      </div>

      {/* Main Content - relative positioned above the backgrounds */}
      <div className="relative z-10 flex min-h-full w-full px-4 py-10 md:py-16">
        <div
          className={cn(
            'mx-auto my-auto w-full',
            isWide ? 'max-w-2xl' : 'max-w-md',
          )}
        >
          {/* Logo Section */}
          <div className="mb-8 text-center">
            <h1 className="font-kronaone text-3xl font-light tracking-wide text-foreground">
              scrambl
            </h1>
          </div>

          {/* Modern Glass Card Container with enhanced contrast */}
          <div className="rounded-card overflow-hidden border border-border/50 bg-card shadow-2xl">
            <div className={isWide ? 'p-6 sm:p-10' : 'p-8'}>{children}</div>
          </div>

          {/* Testimonial */}
          {showTestimonial && (
            <div className="rounded-card mt-8 border border-border/40 bg-card p-6 shadow-lg">
              <p className="mb-4 text-sm italic text-muted-foreground">
                &quot;Scrambl transformed how our team collaborates. It&apos;s
                intuitive, powerful, and has cut our admin time in half.&quot;
              </p>
              <div className="flex items-center">
                <div className="mr-3 h-8 w-8 flex-shrink-0 rounded-full bg-muted"></div>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Sarah Johnson
                  </p>
                  <p className="text-xs text-muted-foreground">
                    CEO, Acme Design
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 space-y-3 text-center">
            <div className="flex items-center justify-center space-x-6 text-xs">
              <a
                href="https://scrambl.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground underline-offset-4 transition-colors duration-200 hover:text-foreground hover:underline"
              >
                Terms of Service
              </a>
              <span className="text-muted-foreground/50">•</span>
              <a
                href="https://scrambl.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground underline-offset-4 transition-colors duration-200 hover:text-foreground hover:underline"
              >
                Privacy Policy
              </a>
              <span className="text-muted-foreground/50">•</span>
              <a
                href="/beta"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground underline-offset-4 transition-colors duration-200 hover:text-foreground hover:underline"
              >
                Beta Services
              </a>
            </div>
            <p className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} Scrambl. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
